import React, { useState, useRef, useEffect } from "react";
import {
  Box,
  Heading,
  Grid,
  GridItem,
  FormControl,
  FormLabel,
  Input,
  useToast,
  FormErrorMessage,
  Center,
  Spinner,
  Button,
  Flex,
} from "@chakra-ui/react";
import Select from "react-select";
import { AsyncPaginate } from "react-select-async-paginate";
import { FaFileUpload, FaImage, FaPaperclip, FaTrash, FaChevronDown, FaCloudUploadAlt } from "react-icons/fa";
import { X, Trash2, GripVertical } from "lucide-react";
import { FormHeader } from "../../../../ui/FormHeader";
import { useNavigate, useLocation } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { addRawMaterial, updateRawMaterial, getRawMaterialDetailById, addRawMaterailinZohoBooks } from "../../../../redux/api/admin/rawMaterialApi";
import { addRawMaterialAction, updateRawMaterialAction } from "../../../../redux/reduxSlice/admin/RawMaterialSlice";
import { getColoursList } from "../../../../redux/reduxApi/admin/ColourReduxApi";
import { getCategoryList } from "../../../../redux/reduxApi/admin/CategoryReduxApi";
import { getRawMaterialCollectionList } from "../../../../redux/reduxApi/admin/RawMaterialCollectionReduxApi";
import { getVendorListAll } from "../../../../redux/reduxApi/admin/VendorReduxApi";
import { taxOptions, rawMaterialUnitOptions } from "../../../AdminPannel/utils/SearchPickList";
import { getLoggedInUserId } from "../../../../../utils/authUtils";
import useShadeSearch from "../../../../hook/useShadeSearch";
import { formatMaterialType } from "../../../../../utils/commonFunctions";

export default function RawMaterialForm() {
  const navigate = useNavigate();
  const colourList = useSelector((state) => state.colour.colour);
  const categoryList = useSelector((state) => state.category.category);
  const rawMaterialCollectionList = useSelector((state) => state.rawMaterialCollection.rawMaterialCollection);
  const vendorList = useSelector((state) => state.vendor.vendor);

  const location = useLocation();
  const toast = useToast();
  const dispatch = useDispatch();
  const loggedInUserId = getLoggedInUserId();
  const fileInputRef = useRef(null);
  const [imagesPopupOpen, setImagesPopupOpen] = useState(false);
  const imagesPopupRef = useRef(null);
  const paperclipButtonRef = useRef(null);

  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [loading, setLoading] = useState(false);
  const [dragOverKey, setDragOverKey] = useState(null);
  const [dragItemType, setDragItemType] = useState(null);
  const [draggedImageIndex, setDraggedImageIndex] = useState(null);
  const [colourOptions, setColourOptions] = useState([]);
  const [categoryOptions, setCategoryOptions] = useState([]);
  const [rawMaterialCollectionOptions, setRawMaterialCollectionOptions] = useState([]);
  const [vendorOptions, setVendorOptions] = useState([]);

  const isUpdateMode = location.state?.isUpdateMode;
  const rawMaterialId = location.state?.id;
  const { loadShades } = useShadeSearch({ categoryOptions });

  const [formData, setFormData] = useState({
    itemName: "",
    productName: "",
    materialType: "",
    unit: "",
    consumptionUnit: "",
    sku: "",
    purchasePrice: "",
    hsnCode: "",
    tax: "",
    colour: "",
    shade: null,
    opening: 0,
    inWord: "",
    outWord: "",
    intransit: "",
    available: "",
    itemImages: [],
    newImages: [],
    removeImages: [],
  });

  // Vendor table state
  const [vendorRows, setVendorRows] = useState([
    { vendor: "", vendorShadeNumber: "", vendorPurchasePrice: "" }
  ]);
  const [originalVendors, setOriginalVendors] = useState([]);
  const [vendorsToDelete, setVendorsToDelete] = useState([]);

  // ---- useEffect hooks for fetching dropdown data ----
  useEffect(() => {
    dispatch(getColoursList());
  }, [dispatch]);

  useEffect(() => {
    dispatch(getCategoryList({ page: 1, size: 1000, limit: 1000 }));
  }, [dispatch]);

  useEffect(() => {
    dispatch(getRawMaterialCollectionList());
  }, [dispatch]);

  useEffect(() => {
    dispatch(getVendorListAll({ page: 1, size: 1000, limit: 1000 }));
  }, [dispatch]);

  useEffect(() => {
    if (colourList && colourList.length > 0) {
      const options = colourList.map(colour => ({
        value: colour.ROWID,
        label: colour.colour
      }));
      setColourOptions(options);
    }
  }, [colourList]);

  useEffect(() => {
    if (categoryList && categoryList.length > 0) {
      const options = categoryList.map(category => ({
        value: category.ROWID,
        label: category.name
      }));
      setCategoryOptions(options);
    }
  }, [categoryList]);

  useEffect(() => {
    if (!formData.shade?.value || !formData.shade?.shadeCategory || categoryOptions.length === 0) return;

    const categoryLabel = categoryOptions.find(
      option => String(option.value) === String(formData.shade.shadeCategory)
    )?.label;

    if (!categoryLabel || formData.shade.label?.includes(" - ")) return;

    setFormData(prev => ({
      ...prev,
      shade: {
        ...prev.shade,
        label: [prev.shade.label, categoryLabel].filter(Boolean).join(" - "),
      },
    }));
  }, [categoryOptions, formData.shade]);

  useEffect(() => {
    if (rawMaterialCollectionList && rawMaterialCollectionList.length > 0) {
      const options = rawMaterialCollectionList.map(item => ({
        value: item.rawMaterialCollection?.ROWID,
        label: formatMaterialType(item.rawMaterialCollection?.materialType)
      }));
      setRawMaterialCollectionOptions(options);
    }
  }, [rawMaterialCollectionList]);

  useEffect(() => {
    if (vendorList && vendorList.length > 0) {
      const options = vendorList.map((v) => ({
        value: v.ROWID,
        label: [v.firstName, v.lastName].filter(Boolean).join(" ") || v.email || "Unknown",
      }));
      setVendorOptions(options);
    }
  }, [vendorList]);

  // ---- Auto-generate productName ----
  useEffect(() => {
    if (isUpdateMode) return; // Don't auto-generate in update mode
    
    const { sku, itemName, colour, width, length, unit } = formData;
    
    // Get colour label
    const colourLabel = colourOptions.find(opt => opt.value === colour)?.label || '';
    
    // Get unit label
    const unitLabel = rawMaterialUnitOptions.find(opt => opt.value === unit)?.value || '';
    
    // Build the product name
    let generatedName = '';
    
    if (sku && sku.trim() !== '') {
      generatedName += sku.trim();
    }
    
    if (itemName && itemName.trim() !== '') {
      generatedName += generatedName ? '-' : '';
      generatedName += itemName.trim();
    }
    
    if (colourLabel && colourLabel.trim() !== '') {
      generatedName += generatedName ? '-' : '';
      generatedName += colourLabel.trim();
    }
    
    // Add dimensions if width and length exist
    if (width && width.trim() !== '' && length && length.trim() !== '') {
      const dimensionPart = `${width.trim()}${unitLabel}*${length.trim()}${unitLabel}`;
      generatedName += generatedName ? '-' : '';
      generatedName += `(${dimensionPart})`;
    } else if (width && width.trim() !== '') {
      const dimensionPart = `${width.trim()}${unitLabel}`;
      generatedName += generatedName ? '-' : '';
      generatedName += `(${dimensionPart})`;
    } else if (length && length.trim() !== '') {
      const dimensionPart = `${length.trim()}${unitLabel}`;
      generatedName += generatedName ? '-' : '';
      generatedName += `(${dimensionPart})`;
    }
    
    // Only update if the generated name is different from current
    if (generatedName !== formData.productName) {
      setFormData(prev => ({ ...prev, productName: generatedName }));
    }
  }, [formData.sku, formData.itemName, formData.colour, formData.width, formData.length, formData.unit, colourOptions, isUpdateMode]);

  // ---- Validation ----
  const validateForm = () => {
    const newErrors = {};
    // if (!formData.itemName?.trim()) {
    //   newErrors.itemName = "Item Name is required";
    // }
    if (!formData.productName?.trim()) {
      newErrors.productName = "Material Name is required";
    }
    if (!formData.materialType) {
      newErrors.materialType = "Material Type is required";
    }
    const selectedMaterial = rawMaterialCollectionOptions.find(opt => opt.value === formData.materialType);
    if (selectedMaterial?.label?.toLowerCase() === "fabric" && !formData.shade) {
      newErrors.shade = "Shade is required for Fabric";
    }
    // if (!formData.unit) {
    //   newErrors.unit = "Unit is required";
    // }
    if (!formData.sku?.trim()) {
      newErrors.sku = "SKU is required";
    }
    // if (!formData.purchasePrice?.trim()) {
    //   newErrors.purchasePrice = "Purchase Price is required";
    // }
    // if (!formData.opening?.trim()) {
    //   newErrors.opening = "Opening is required";
    // }
    // if (!formData.available?.trim()) {
    //   newErrors.available = "Available is required";
    // }
    if (!formData.hsnCode?.trim()) {
      newErrors.hsnCode = "HSN Code is required";
    }
    if (!formData.tax) {
      newErrors.tax = "Tax is required";
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // ---- Fetch data for update ----
  useEffect(() => {
    if (isUpdateMode && rawMaterialId) {
      setLoading(true);
      getRawMaterialDetailById(rawMaterialId)
        .then((response) => {
          if (response.data && response.data.data) {
            const rawMaterialData = response.data.data;
            const vendorDetails = rawMaterialData.items || [];

            // Parse media
            let parsedMedia = [];
            if (rawMaterialData.img) {
              try {
                if (typeof rawMaterialData.img === 'string') {
                  const parsed = JSON.parse(rawMaterialData.img);
                  if (Array.isArray(parsed)) {
                    parsedMedia = parsed
                      .filter(item => item && (typeof item === 'string' || item.url))
                      .map(item => ({
                        id: typeof item === 'string' ? item.split('/').pop() : item.id,
                        name: typeof item === 'string' ? item.split('/').pop() : item.name,
                        url: typeof item === 'string' ? item : item.url,
                        result: true
                      }));
                  }
                } else if (Array.isArray(rawMaterialData.media)) {
                  parsedMedia = rawMaterialData.media.map(item => ({
                    id: item.id || (item.url ? item.url.split('/').pop() : Date.now()),
                    name: item.name || 'image',
                    url: item.url || item,
                    result: true
                  }));
                }
              } catch (error) {
                console.error("Error parsing media:", error);
              }
            }

            // Vendor rows
            let vendorRowsData = [];
            if (vendorDetails && Array.isArray(vendorDetails)) {
              vendorRowsData = vendorDetails.map(vendor => ({
                vendor: vendor.vender?.ROWID || "",
                vendorShadeNumber: vendor.venderShadeNo || "",
                vendorPurchasePrice: vendor.purchasePrize?.toString() || ""
              }));
              setOriginalVendors(vendorRowsData);
            }
            if (vendorRowsData.length === 0) {
              vendorRowsData = [{ vendor: "", vendorShadeNumber: "", vendorPurchasePrice: "" }];
            }
            setVendorRows(vendorRowsData);

            setFormData({
              itemName: rawMaterialData.itemName || "",
              productName: rawMaterialData.productName || "",
              materialType: rawMaterialData.materialType?.ROWID || "",
              unit: rawMaterialData.unit || "",
              sku: rawMaterialData.sku || "",
              purchasePrice: rawMaterialData.purchasePrice || "",
              intransit: rawMaterialData.intransit || "",
              outWord: rawMaterialData.outWord || "",
              inWord: rawMaterialData.inWord || "",
              opening: rawMaterialData.opening || "",
              available: rawMaterialData.available || "",
              hsnCode: rawMaterialData.hsnCode || "",
              tax: rawMaterialData.tax || "",
              colour: rawMaterialData.colour?.ROWID || "",
              width: rawMaterialData.width || "",
              length: rawMaterialData.length || "",
              consumptionUnit: rawMaterialData.consumptionUnit || "",
                  shade: rawMaterialData.shade?.ROWID
                ? {
                    value: rawMaterialData.shade.ROWID,
                    shadeCategory: rawMaterialData.shade.shadeCategory,
                    label: [
                      rawMaterialData.shade.shadeNo,
                      rawMaterialData.shade.shadeCategory?.name ||
                        categoryOptions.find(option => String(option.value) === String(rawMaterialData.shade.shadeCategory))?.label ||
                        "",
                    ].filter(Boolean).join(" - "),
                  }
                : null,
              itemImages: parsedMedia,
              newImages: [],
              removeImages: [],
            });
          }
        })
        .catch((error) => {
          console.error("Error fetching raw material details:", error);
          toast({
            title: "Error fetching details",
            description: error.message || "Please try again",
            status: "error",
            duration: 3000,
            isClosable: true,
          });
        })
        .finally(() => setLoading(false));
    }
  }, [isUpdateMode, rawMaterialId, toast]);

  // ---- Handlers ----
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (errors[name]) setErrors(prev => ({ ...prev, [name]: "" }));
  };

  const handleSelectChange = (name, selectedOption) => {
    const value = selectedOption ? selectedOption.value : "";
    const updatedData = { ...formData, [name]: value };

    if (name === "materialType") {
      if (selectedOption && selectedOption.label.toLowerCase() === "fabric") {
        updatedData.shade = null;
        updatedData.itemName = "";
      } else {
        updatedData.itemName = selectedOption ? selectedOption.label : "";
        updatedData.shade = null;
      }
    }

    setFormData(updatedData);
    if (errors[name]) setErrors(prev => ({ ...prev, [name]: "" }));
  };

  const handleShadeChange = (selectedOption) => {
    setFormData(prev => ({
      ...prev,
      shade: selectedOption ?? null,
      itemName: selectedOption ? selectedOption.label : "",
    }));
    if (errors.shade) setErrors(prev => ({ ...prev, shade: "" }));
  };

  const handleAddVendorRow = () => {
    setVendorRows([...vendorRows, { vendor: "", vendorShadeNumber: "", vendorPurchasePrice: "" }]);
  };

  const handleDeleteVendorRow = (index) => {
    const rowToDelete = vendorRows[index];
    if (isUpdateMode && rawMaterialId && rowToDelete.vendor) {
      if (originalVendors.some(v => v.vendor === rowToDelete.vendor)) {
        setVendorsToDelete(prev => [...prev, rowToDelete.vendor]);
      }
    }
    const updatedRows = vendorRows.filter((_, i) => i !== index);
    setVendorRows(updatedRows);
  };

  const handleVendorSelectChange = (index, field, selectedOption) => {
    const value = selectedOption ? selectedOption.value : "";
    const updatedRows = [...vendorRows];
    updatedRows[index][field] = value;
    setVendorRows(updatedRows);
  };

  const handleVendorInputChange = (index, field, value) => {
    const updatedRows = [...vendorRows];
    updatedRows[index][field] = value;
    setVendorRows(updatedRows);
  };

  // ---- Image compression & upload ----
  const compressImage = (file, maxSize) => {
    return new Promise((resolve) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = (event) => {
        const img = new Image();
        img.src = event.target.result;
        img.onload = () => {
          const canvas = document.createElement("canvas");
          let width = img.width;
          let height = img.height;
          const maxWidth = 1920;
          if (width > maxWidth) {
            height = (height * maxWidth) / width;
            width = maxWidth;
          }
          canvas.width = width;
          canvas.height = height;
          const ctx = canvas.getContext("2d");
          ctx.drawImage(img, 0, 0, width, height);
          let quality = 0.9;
          const tryCompress = () => {
            canvas.toBlob(
              (blob) => {
                if (blob.size > maxSize && quality > 0.1) {
                  quality -= 0.1;
                  tryCompress();
                } else {
                  const compressedFile = new File([blob], file.name, {
                    type: file.type,
                    lastModified: Date.now(),
                  });
                  resolve(compressedFile);
                }
              },
              file.type,
              quality
            );
          };
          tryCompress();
        };
      };
    });
  };

  const handleImageUpload = async (e) => {
    const files = Array.from(e.target.files);
    const imageFiles = files.filter((file) => file.type.startsWith("image/"));

    if (imageFiles.length !== files.length) {
      toast({
        title: "Only image files are allowed",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    const totalImages = formData.itemImages.length + formData.newImages.length + imageFiles.length;
    if (totalImages > 5) {
      toast({
        title: "You can only upload a maximum of 5 images",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    const maxSize = 3 * 1024 * 1024;
    const processedFiles = await Promise.all(
      imageFiles.map(async (file) => {
        if (file.size > maxSize) {
          toast({ title: `Compressing ${file.name}...`, status: "info", duration: 2000 });
          return await compressImage(file, maxSize);
        }
        return file;
      })
    );

    const newFiles = processedFiles.map((file) => ({
      file,
      url: URL.createObjectURL(file),
      name: file.name,
      type: file.type,
    }));

    setFormData(prev => ({ ...prev, newImages: [...prev.newImages, ...newFiles] }));
    toast({
      title: `${processedFiles.length} image(s) uploaded successfully`,
      status: "success",
      duration: 3000,
    });
  };

  // ---- Drag and drop ----
  const handleDragStart = (e, index, type) => {
    e.dataTransfer.effectAllowed = 'move';
    setDraggedImageIndex(index);
    setDragItemType(type);
    e.dataTransfer.setData('text/plain', `${type}-${index}`);
  };

  const handleDragOver = (e, index, type) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    const key = `${type}-${index}`;
    if (dragOverKey !== key) setDragOverKey(key);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setDragOverKey(null);
  };

  const handleDragEnd = () => {
    setDragOverKey(null);
    setDraggedImageIndex(null);
    setDragItemType(null);
  };

  const handleDrop = (e, targetIndex, targetType) => {
    e.preventDefault();
    if (draggedImageIndex === null || dragItemType !== targetType) {
      setDragOverKey(null);
      setDraggedImageIndex(null);
      setDragItemType(null);
      return;
    }

    if (draggedImageIndex === targetIndex) {
      setDragOverKey(null);
      setDraggedImageIndex(null);
      setDragItemType(null);
      return;
    }

    if (dragItemType === 'existing' && targetType === 'existing') {
      const updatedImages = [...formData.itemImages];
      const [draggedImage] = updatedImages.splice(draggedImageIndex, 1);
      updatedImages.splice(targetIndex, 0, draggedImage);
      setFormData(prev => ({ ...prev, itemImages: updatedImages }));
    } else if (dragItemType === 'new' && targetType === 'new') {
      const updatedImages = [...formData.newImages];
      const [draggedImage] = updatedImages.splice(draggedImageIndex, 1);
      updatedImages.splice(targetIndex, 0, draggedImage);
      setFormData(prev => ({ ...prev, newImages: updatedImages }));
    }

    setDragOverKey(null);
    setDraggedImageIndex(null);
    setDragItemType(null);
  };

  const handleRemoveImage = (index, type) => {
    if (type === "existing") {
      const imageToRemove = formData.itemImages[index];
      setFormData(prev => ({
        ...prev,
        itemImages: prev.itemImages.filter((_, i) => i !== index),
        removeImages: [...prev.removeImages, imageToRemove.id],
      }));
    } else {
      URL.revokeObjectURL(formData.newImages[index].url);
      setFormData(prev => ({
        ...prev,
        newImages: prev.newImages.filter((_, i) => i !== index),
      }));
    }
  };

  // Close popup on outside click
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (!imagesPopupOpen) return;
      const popup = imagesPopupRef.current;
      const button = paperclipButtonRef.current;
      if (popup && !popup.contains(e.target) && button && !button.contains(e.target)) {
        setImagesPopupOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [imagesPopupOpen]);

  const totalImages = (formData.itemImages?.length || 0) + (formData.newImages?.length || 0);

  // ---- Submit ----
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (isSubmitting) return;

    if (!validateForm()) {
      toast({
        title: "Please fill in all required fields",
        status: "error",
        duration: 3000,
      });
      return;
    }

    setIsSubmitting(true);
    setLoading(true);

    try {
      const submitData = new FormData();

      // Text fields
      submitData.append("itemName", formData.itemName);
      submitData.append("productName", formData.productName);
      submitData.append("materialType", formData.materialType);
      submitData.append("width", formData.width || "");
      submitData.append("length", formData.length || "");
      submitData.append("unit", formData.unit);
      submitData.append("consumptionUnit", formData.consumptionUnit);
      submitData.append("sku", formData.sku);
      submitData.append("purchasePrice", formData.purchasePrice);
      submitData.append("inWord", formData.inWord);
      submitData.append("opening", formData.opening);
      submitData.append("outWord", formData.outWord);
      submitData.append("intransit", formData.intransit);
      submitData.append("available", formData.available);
      submitData.append("hsnCode", formData.hsnCode);
      submitData.append("createdBy", formData.createdBy || loggedInUserId || "");
      submitData.append("modifiedBy", loggedInUserId || "");
      submitData.append("tax", formData.tax);
      submitData.append("colour", formData.colour);
      if (formData.shade) {
        submitData.append("shadeNo", formData.shade.value);
      }

      // Vendor details
      const validVendors = vendorRows
        .filter(row => row.vendor && row.vendorShadeNumber && row.vendorPurchasePrice)
        .map(row => ({
          vendorId: row.vendor,
          vendorShadeNumber: row.vendorShadeNumber,
          vendorPurchasePrice: Number(row.vendorPurchasePrice)
        }));
      if (validVendors.length > 0) {
        submitData.append("vendorDetails", JSON.stringify(validVendors));
      }

      if (vendorsToDelete.length > 0) {
        submitData.append("removeVendors", JSON.stringify(vendorsToDelete));
      }

      // Media
      const orderedExistingImages = formData.itemImages
        .map((image) => image.url)
        .filter(url => url && url.trim() !== "");

      if (orderedExistingImages.length > 0) {
        submitData.append("existingMediaOrder", JSON.stringify(orderedExistingImages));
      }

      if (formData.removeImages.length > 0) {
        submitData.append("imgRowId", JSON.stringify(formData.removeImages));
      }

      formData.newImages.forEach((imageObj) => {
        submitData.append("media", imageObj.file);
      });

      let response;
      if (isUpdateMode && rawMaterialId) {
        submitData.append("ROWID", rawMaterialId);
        response = await updateRawMaterial(rawMaterialId, submitData);

        if (response?.data?.success || response?.status === 200) {
          dispatch(updateRawMaterialAction({ ROWID: rawMaterialId, ...formData }));
          toast({
            title: "Success",
            description: "Raw Material updated successfully",
            status: "success",
            duration: 3000,
          });
          navigate('/admin/inventory/raw-material-list');
        } else {
          throw new Error(response?.data?.message || "Update failed");
        }
      } else {
        response = await addRawMaterial(submitData);

        if (response?.data?.success || response?.status === 200 || response?.data?.data) {
          setFormData({
            itemName: "",
            productName: "",
            materialType: "",
            unit: "",
            sku: "",
            purchasePrice: "",
            opening: "",
            inWord: "",
            outWord: "",
            intransit: "",
            available: "",
            hsnCode: "",
            tax: "",
            colour: "",
            itemImages: [],
            newImages: [],
            removeImages: [],
            shade: null,
          });

          setVendorRows([{ vendor: "", vendorShadeNumber: "", vendorPurchasePrice: "" }]);
          setOriginalVendors([]);
          setVendorsToDelete([]);

          if (response?.data) {
            const payload = {
              "type": "rawMaterial"
            }
            const res = await addRawMaterailinZohoBooks(response?.data?.parentId, payload);
            console.log("zoho books client response", res)
          }

          toast({
            title: "Success",
            description: "Raw Material created successfully",
            status: "success",
            duration: 3000,
          });

          navigate('/admin/inventory/raw-material-list');
        } else {
          throw new Error(response?.data?.message || "Creation failed");
        }
      }
    } catch (error) {
      console.error('Error saving raw material:', error);
      toast({
        title: "Error",
        description: error.response?.data?.message || error.message || `Failed to ${isUpdateMode ? 'update' : 'create'} raw material`,
        status: "error",
        duration: 5000,
      });
    } finally {
      setIsSubmitting(false);
      setLoading(false);
    }
  };

  // ---- Render ----
  return (
    <Box className="h-[93vh] flex flex-col">
      {loading && (
        <Center flex="1" flexDirection="column" gap={4}>
          <Spinner size="xl" color="#011638" />
        </Center>
      )}

      {!loading && (
        <>
          <Box className="sticky top-0 z-10 bg-white">
            <FormHeader
              heading="Raw Material"
              onSubmit={handleSubmit}
              isUpdateMode={isUpdateMode}
              isSubmitting={isSubmitting}
            />
          </Box>

          <Box className="flex-1 overflow-y-auto p-4 md:p-6">
            {/* Card 1: Raw Material Information */}
            <Box p={{ base: 4, md: 6 }} borderWidth="1px" borderRadius="lg" bg="white" boxShadow="sm">
              <Heading size="md" mb={4}>Raw Material Information</Heading>

              <Flex direction={{ base: "column", md: "row" }} gap={7} w="100%">
                <Box w={{ base: "100%", md: "50%" }}>
                  <Grid templateColumns="1fr" gap={4}>
                    {/* New Item Name field */}
                    {!isUpdateMode && (
                      <GridItem>
                        <FormControl isInvalid={errors.itemName}>
                          <FormLabel>Item Name<span style={{ color: "red" }}>*</span></FormLabel>
                          <Input 
                            name="itemName" 
                            placeholder="Enter Item Name" 
                            value={formData.itemName} 
                            onChange={handleChange}
                          />
                          <FormErrorMessage>{errors.itemName}</FormErrorMessage>
                        </FormControl>
                      </GridItem>
                    )}

                    <GridItem>
                      <FormControl isInvalid={errors.productName}>
                        <FormLabel>Material Name<span style={{ color: "red" }}>*</span></FormLabel>
                        <Input 
                          name="productName" 
                          placeholder="Auto-generated Material Name" 
                          value={formData.productName} 
                          isDisabled={true}
                          bg="gray.50"
                        />
                        <FormErrorMessage>{errors.productName}</FormErrorMessage>
                      </FormControl>
                    </GridItem>

                    <GridItem>
                      <FormControl isInvalid={errors.materialType}>
                        <FormLabel>Material Type<span style={{ color: "red" }}>*</span></FormLabel>
                        <Select
                          options={rawMaterialCollectionOptions}
                          name="materialType"
                          placeholder="Select Material Type"
                          isSearchable={true}
                          isClearable
                          isDisabled={isSubmitting}
                          onChange={(option) => handleSelectChange("materialType", option)}
                          value={rawMaterialCollectionOptions.find(option => option.value == formData.materialType)}
                          styles={{
                            control: (base, state) => ({
                              ...base,
                              cursor: "pointer",
                              borderColor: errors.materialType ? "#E53E3E" : base.borderColor,
                              "&:hover": {
                                borderColor: errors.materialType ? "#E53E3E" : base.borderColor,
                              },
                            }),
                            option: (base) => ({
                              ...base,
                              cursor: "pointer",
                            }),
                          }}
                        />
                        <FormErrorMessage>{errors.materialType}</FormErrorMessage>
                      </FormControl>
                    </GridItem>
                    <GridItem>
                      <FormControl isInvalid={errors.shade}>
                        <FormLabel>
                          Shade
                          {rawMaterialCollectionOptions.find(option => option.value == formData.materialType)?.label?.toLowerCase() === "fabric" && (
                            <span style={{ color: "red" }}>*</span>
                          )}
                        </FormLabel>
                        <AsyncPaginate
                          value={formData.shade}
                          loadOptions={loadShades}
                          onChange={handleShadeChange}
                          placeholder="Search shade"
                          additional={{ page: 1 }}
                          isClearable
                          isDisabled={isSubmitting}
                          styles={{
                            control: (base) => ({
                              ...base,
                              cursor: "pointer",
                              borderColor: errors.shade ? "#E53E3E" : base.borderColor,
                              "&:hover": {
                                borderColor: errors.shade ? "#E53E3E" : base.borderColor,
                              },
                            }),
                            option: (base) => ({
                              ...base,
                              cursor: "pointer",
                            }),
                          }}
                        />
                        <FormErrorMessage>{errors.shade}</FormErrorMessage>
                      </FormControl>
                    </GridItem>
                    
                    <GridItem>
                      <FormControl isInvalid={errors.length}>
                        <FormLabel>Length</FormLabel>
                        <Input type="number" name="length" placeholder="Length" value={formData.length || ""} onChange={handleChange} />
                      </FormControl>
                    </GridItem>
                    
                    <GridItem>
                      <FormControl isInvalid={errors.width}>
                        <FormLabel>Width</FormLabel>
                        <Input type="text" name="width" placeholder="Width" value={formData.width ||""} onChange={handleChange} />
                      </FormControl>
                    </GridItem>

                    <GridItem>
                      <FormControl>
                        <FormLabel>Unit</FormLabel>
                        <Select
                          options={rawMaterialUnitOptions}
                          name="unit"
                          placeholder="Select Unit"
                          isSearchable={true}
                          isClearable
                          isDisabled={isSubmitting}
                          onChange={(option) => handleSelectChange("unit", option)}
                          value={rawMaterialUnitOptions.find(option => option.value === formData.unit)}
                          styles={{
                            control: (base, state) => ({
                              ...base,
                              cursor: "pointer",
                              borderColor: errors.unit ? "#E53E3E" : base.borderColor,
                              "&:hover": {
                                borderColor: errors.unit ? "#E53E3E" : base.borderColor,
                              },
                            }),
                            option: (base) => ({
                              ...base,
                              cursor: "pointer",
                            }),
                          }}
                        />
                        {/* <FormErrorMessage>{errors.unit}</FormErrorMessage> */}
                      </FormControl>
                    </GridItem>

                    <GridItem>
                      <FormControl isInvalid={errors.colour}>
                        <FormLabel>Colour</FormLabel>
                        <Select
                          options={colourOptions}
                          name="colour"
                          placeholder="Select Colour"
                          isSearchable={true}
                          isClearable
                          isDisabled={isSubmitting}
                          onChange={(option) => handleSelectChange("colour", option)}
                          value={colourOptions.find(option => option.value === formData.colour)}
                          styles={{
                            control: (base, state) => ({
                              ...base,
                              cursor: "pointer",
                              borderColor: errors.colour ? "#E53E3E" : base.borderColor,
                              "&:hover": {
                                borderColor: errors.colour ? "#E53E3E" : base.borderColor,
                              },
                            }),
                            option: (base) => ({
                              ...base,
                              cursor: "pointer",
                            }),
                          }}
                        />
                      </FormControl>
                    </GridItem>
                  </Grid>
                </Box>

                <Box w={{ base: "100%", md: "50%" }}>
                  <GridItem mb={3}>
                    <FormLabel>Media</FormLabel>
                    <Input ref={fileInputRef} type="file" id="imageUpload" multiple accept="image/*" className="hidden" onChange={handleImageUpload} />

                    {totalImages === 0 ? (
                      <label htmlFor="imageUpload" className="flex flex-col justify-center items-center w-full h-[180px] border-2 border-dashed border-gray-300 rounded-lg bg-gray-50 cursor-pointer transition-all duration-200 hover:border-blue-900 hover:bg-blue-50">
                        <div className="w-[70px] h-[70px] bg-gradient-to-br from-blue-950 to-blue-900 rounded-xl flex justify-center items-center mb-3">
                          <FaFileUpload size={36} color="white" />
                        </div>
                        <div className="text-sm text-gray-700 mb-1">
                          <span className="text-blue-950 font-semibold">Click to Upload Images</span>
                        </div>
                        <div className="text-xs text-gray-500">
                          (Max <span className="font-bold text-blue-900">5</span> images)
                        </div>
                      </label>
                    ) : (
                      <div className="flex gap-3 items-center mt-2 flex-wrap">
                        {formData.itemImages.map((image, index) => (
                          <div key={image.id || image.url || index} className={`relative group ${dragOverKey === `existing-${index}` ? 'ring-2 ring-blue-500' : ''}`}
                            draggable onDragStart={(e) => handleDragStart(e, index, 'existing')}
                            onDragOver={(e) => handleDragOver(e, index, 'existing')} onDragLeave={handleDragLeave}
                            onDrop={(e) => handleDrop(e, index, 'existing')} onDragEnd={handleDragEnd}>
                            <div className="relative">
                              <img src={image.url} alt={image.name} className="w-16 h-16 object-cover rounded border cursor-move" />
                              <div className="absolute top-1 left-1 bg-black bg-opacity-50 rounded p-0.5 cursor-grab"><GripVertical size={12} className="text-white" /></div>
                            </div>
                            <button type="button" onClick={() => handleRemoveImage(index, "existing")}
                              className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full w-6 h-6 flex items-center justify-center opacity-0 group-hover:opacity-100"><X size={14} /></button>
                          </div>
                        ))}
                        {formData.newImages.map((file, index) => (
                          <div key={`new-${file.name}-${index}`} className={`relative group ${dragOverKey === `new-${index}` ? 'ring-2 ring-blue-500' : ''}`}
                            draggable onDragStart={(e) => handleDragStart(e, index, 'new')}
                            onDragOver={(e) => handleDragOver(e, index, 'new')} onDragLeave={handleDragLeave}
                            onDrop={(e) => handleDrop(e, index, 'new')} onDragEnd={handleDragEnd}>
                            <div className="relative">
                              <img src={file.url} alt={file.name} className="w-16 h-16 object-cover rounded border cursor-move" />
                              <div className="absolute top-1 left-1 bg-black bg-opacity-50 rounded p-0.5 cursor-grab"><GripVertical size={12} className="text-white" /></div>
                            </div>
                            <button type="button" onClick={() => handleRemoveImage(index, "new")}
                              className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full w-6 h-6 flex items-center justify-center opacity-0 group-hover:opacity-100"><X size={14} /></button>
                          </div>
                        ))}
                        <div className="relative">
                          <button ref={paperclipButtonRef} type="button" className="flex items-center gap-2 px-2 py-1.5 text-white bg-blue-950 rounded-lg" onClick={() => setImagesPopupOpen(p => !p)}>
                            <FaPaperclip size={16} /><span>{totalImages}</span>
                          </button>
                          <div ref={imagesPopupRef} className={`absolute top-full right-0 mt-2 w-[400px] bg-white rounded-lg shadow-lg border z-10 ${imagesPopupOpen ? "" : "hidden"}`}>
                            <div className="flex justify-between px-4 py-2 border-b"><span className="font-semibold">Images ({totalImages}/5)</span><button onClick={() => setImagesPopupOpen(false)}>×</button></div>
                            <div className="max-h-[300px] overflow-y-auto p-2">
                              {formData.itemImages.map((img, idx) => (
                                <div key={img.id || idx} className="flex items-center gap-3 py-2 px-2 hover:bg-gray-50 border-b">
                                  <FaImage className="text-blue-900" /><span className="flex-1 truncate text-sm">{img.name}</span>
                                  <button onClick={() => handleRemoveImage(idx, "existing")} className="p-2 text-red-600"><FaTrash size={14} /></button>
                                </div>
                              ))}
                              {formData.newImages.map((file, idx) => (
                                <div key={idx} className="flex items-center gap-3 py-2 px-2 hover:bg-gray-50 border-b">
                                  <FaImage className="text-green-900" /><span className="flex-1 truncate text-sm">{file.name}</span>
                                  <button onClick={() => handleRemoveImage(idx, "new")} className="p-2 text-red-600"><FaTrash size={14} /></button>
                                </div>
                              ))}
                            </div>
                          </div>
                        </div>
                      </div>
                    )}
                    {totalImages > 0 && (
                      <Button leftIcon={<FaCloudUploadAlt />} rightIcon={<FaChevronDown />} variant="outline" border="2px dashed" mt={2}
                        onClick={() => fileInputRef.current?.click()}>Upload Images</Button>
                    )}
                  </GridItem>
                </Box>
              </Flex>

              <Box mt={4}>
                <Grid templateColumns="repeat(2, 1fr)" gap={4}>
                  <FormControl isInvalid={errors.sku}>
                    <FormLabel>SKU<span style={{ color: "red" }}>*</span></FormLabel>
                    <Input name="sku" placeholder="SKU" value={formData.sku} onChange={handleChange} />
                    <FormErrorMessage>{errors.sku}</FormErrorMessage>
                  </FormControl>

                  <FormControl isInvalid={errors.hsnCode}>
                    <FormLabel>HSN Code<span style={{ color: "red" }}>*</span></FormLabel>
                    <Input name="hsnCode" placeholder="HSN Code" value={formData.hsnCode} onChange={handleChange} />
                    <FormErrorMessage>{errors.hsnCode}</FormErrorMessage>
                  </FormControl>

                  <FormControl >
                    <FormLabel>Purchase Price</FormLabel>
                    <Input name="purchasePrice" placeholder="Purchase Price" value={formData.purchasePrice} onChange={handleChange} />
                  </FormControl>

                  <FormControl isInvalid={errors.tax}>
                    <FormLabel>Tax<span style={{ color: "red" }}>*</span></FormLabel>
                    <Select
                      options={taxOptions}
                      name="tax"
                      placeholder="Select Tax"
                      isSearchable={true}
                      isClearable
                      isDisabled={isSubmitting}
                      onChange={(option) => handleSelectChange("tax", option)}
                      value={taxOptions.find(option => option.value === formData.tax)}
                      styles={{
                        control: (base, state) => ({
                          ...base,
                          cursor: "pointer",
                          borderColor: errors.tax ? "#E53E3E" : base.borderColor,
                          "&:hover": {
                            borderColor: errors.tax ? "#E53E3E" : base.borderColor,
                          },
                        }),
                        option: (base) => ({
                          ...base,
                          cursor: "pointer",
                        }),
                      }}
                    />
                    <FormErrorMessage>{errors.tax}</FormErrorMessage>
                  </FormControl>

                  <FormControl isInvalid={errors.opening}>
                    <FormLabel>Opening</FormLabel>
                    <Input type="number" name="opening" placeholder="Opening" value={formData.opening} onChange={handleChange} />
                  </FormControl>

                  {/* <FormControl>
                    <FormLabel>Inward</FormLabel>
                    <Input type="number" name="inWord" placeholder="Inward" value={formData.inWord} onChange={handleChange} isDisabled={isUpdateMode} />
                  </FormControl>

                  <FormControl>
                    <FormLabel>Outward</FormLabel>
                    <Input type="number" name="outWord" placeholder="Outward" value={formData.outWord} onChange={handleChange} isDisabled={isUpdateMode} />
                  </FormControl>

                  <FormControl>
                    <FormLabel>In Transit</FormLabel>
                    <Input type="number" name="intransit" placeholder="In Transit" value={formData.intransit} onChange={handleChange} isDisabled={isUpdateMode} />
                  </FormControl> */}

                  <FormControl >
                    <FormLabel>Available</FormLabel>
                    <Input type="number" name="available" placeholder="Available" value={formData.available} onChange={handleChange} />
                    {/* <FormErrorMessage>{errors.available}</FormErrorMessage> */}
                  </FormControl>
                      <FormControl>
                        <FormLabel>Consumption Unit</FormLabel>
                        <Select
                          options={rawMaterialUnitOptions}
                          name="consumptionUnit"
                          placeholder="Select Unit"
                          isSearchable={true}
                          isClearable
                          isDisabled={isSubmitting}
                          onChange={(option) => handleSelectChange("consumptionUnit", option)}
                          value={rawMaterialUnitOptions.find(option => option.value === formData.consumptionUnit)}
                          styles={{
                            control: (base, state) => ({
                              ...base,
                              cursor: "pointer",
                              borderColor: errors.unit ? "#E53E3E" : base.borderColor,
                              "&:hover": {
                                borderColor: errors.unit ? "#E53E3E" : base.borderColor,
                              },
                            }),
                            option: (base) => ({
                              ...base,
                              cursor: "pointer",
                            }),
                          }}
                        />
                        {/* <FormErrorMessage>{errors.unit}</FormErrorMessage> */}
                      </FormControl>
                </Grid>
                
              </Box>
            </Box>

            {/* Card 2: Vendor Details */}
            <Box mt={6} p={{ base: 4, md: 6 }} borderWidth="1px" borderRadius="lg" bg="white" boxShadow="sm">
              <Flex justify="space-between" align="center" mb={4}>
                <Heading size="md">Vendor Details</Heading>
                <Button colorScheme="blue" onClick={handleAddVendorRow} bg="#011638" _hover={{ bg: "#033366" }} size="sm">+ Add Vendor</Button>
              </Flex>

              <Box overflowX="auto" overflowY="auto" maxHeight="300px" sx={{ '&::-webkit-scrollbar': { width: '8px' }, '&::-webkit-scrollbar-thumb': { background: '#888', borderRadius: '4px' } }}>
                <table className="table-bordered min-w-[600px] w-full">
                  <thead className="sticky top-0 bg-white">
                    <tr>
                      <th className="whitespace-nowrap w-[10%]">S.No.</th>
                      <th className="whitespace-nowrap w-[30%]">Vendor</th>
                      <th className="whitespace-nowrap w-[30%]">Vendor Shade Number</th>
                      <th className="whitespace-nowrap w-[25%]">Vendor Purchase Price</th>
                      <th className="whitespace-nowrap w-[5%] text-center">Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {vendorRows.map((item, index) => (
                      <tr key={index}>
                        <td className="text-center">{index + 1}</td>
                        <td>
                          <Select
                            options={vendorOptions}
                            placeholder="Select Vendor"
                            isClearable
                            isSearchable
                            value={vendorOptions.find(option => option.value === item.vendor)}
                            onChange={(selected) => handleVendorSelectChange(index, "vendor", selected)}
                            styles={{
                              control: (base) => ({
                                ...base,
                                cursor: "pointer",
                                minWidth: "180px",
                              }),
                              option: (base) => ({
                                ...base,
                                cursor: "pointer",
                              }),
                            }}
                          />
                        </td>
                        <td>
                          <Input
                            placeholder="Vendor Shade Number"
                            value={item.vendorShadeNumber}
                            onChange={(e) => handleVendorInputChange(index, "vendorShadeNumber", e.target.value)}
                          />
                        </td>
                        <td>
                          <Input
                            type="number"
                            placeholder="Purchase Price"
                            value={item.vendorPurchasePrice}
                            onChange={(e) => handleVendorInputChange(index, "vendorPurchasePrice", e.target.value)}
                          />
                        </td>
                        <td className="text-center">
                          <button onClick={() => handleDeleteVendorRow(index)} className="text-red-600" disabled={vendorRows.length <= 1}>
                            <Trash2 size={18} />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </Box>
            </Box>
          </Box>
        </>
      )}
    </Box>
  );
}
